import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import SwitchField from '../common/SwitchField';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';

export const EMAIL_CHECK_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  AVAILABLE: 'available',
  EXISTING: 'existing',
  ALREADY_IN_STORE: 'alreadyInStore',
  ERROR: 'error'
};

const disabledFieldSx = {
  opacity: 0.55,
  pointerEvents: 'none'
};

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email.trim());

const inputStyle = (hasError, isDisabled) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
  cursor: isDisabled ? 'not-allowed' : 'text'
});

const TeamMemberPersonalInfo = ({
  formData,
  updateFormData,
  errors,
  mode = 'create',
  emailCheckStatus = EMAIL_CHECK_STATUS.IDLE,
  checkingEmail = false,
  onCheckEmail,
  onEmailChange,
  isExistingMember = false,
  existingMemberName = '',
  fieldsEnabled = false,
  hideAcceptsBookings = false,
  hideHeading = false,
  savedAcceptsCustomerBookings = true,
}) => {
  const { t } = useTranslation();
  const stoppedTakingBookings = mode === 'update'
    && savedAcceptsCustomerBookings !== false
    && formData.acceptsCustomerBookings === false;
  const startedTakingBookings = mode === 'update'
    && savedAcceptsCustomerBookings === false
    && formData.acceptsCustomerBookings !== false;

  const acceptsCustomerBookingsSwitch = hideAcceptsBookings ? null : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <SwitchField
        checked={formData.acceptsCustomerBookings !== false}
        onChange={(event) => updateFormData({ acceptsCustomerBookings: event.target.checked })}
        label={t('teamMembers.acceptsCustomerBookings.label')}
        helpText={t('teamMembers.acceptsCustomerBookings.help')}
      />
      {mode !== 'update' ? (
        <Typography variant="caption" color="text.secondary">
          {t('teamMembers.acceptsCustomerBookings.chargeNotice')}
        </Typography>
      ) : null}
      {startedTakingBookings ? (
        <Alert severity="info">
          {t('teamMembers.acceptsCustomerBookings.chargeNotice')}
        </Alert>
      ) : null}
      {stoppedTakingBookings ? (
        <Alert severity="warning">
          {t('teamMembers.acceptsCustomerBookings.currentPeriodNotice')}
        </Alert>
      ) : null}
    </Box>
  );

  if (mode === 'update') {
    return (
      <Box>
        {hideHeading ? null : (
          <>
            <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
              {t('teamMembers.registration.steps.step1.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
              {t('teamMembers.registration.steps.step1.description')}
            </Typography>
          </>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.name')} *
              </Typography>
              <input
                type="text"
                autoComplete="name"
                value={formData.name}
                onChange={(e) => updateFormData({ name: e.target.value })}
                placeholder={t('teamMembers.registration.namePlaceholder')}
                style={inputStyle(!!errors.name, false)}
                onFocus={(e) => { e.target.style.borderColor = BRAND.accent; }}
                onBlur={(e) => { e.target.style.borderColor = errors.name ? '#f44336' : BRAND.line; }}
              />
              {errors.name && (
                <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                  {errors.name}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.email')} *
              </Typography>
              <input
                type="email"
                value={formData.email}
                readOnly
                disabled
                style={inputStyle(!!errors.email, true)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.jobTitle')}
              </Typography>
              <input
                type="text"
                value={formData.jobTitle || ''}
                onChange={(e) => updateFormData({ jobTitle: e.target.value })}
                placeholder={t('teamMembers.registration.jobTitlePlaceholder')}
                maxLength={100}
                style={inputStyle(false, false)}
                onFocus={(e) => { e.target.style.borderColor = BRAND.accent; }}
                onBlur={(e) => { e.target.style.borderColor = BRAND.line; }}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.language')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.language}
                  onChange={(e) => updateFormData({ language: e.target.value })}
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    height: '48px !important',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${BRAND.line} !important`,
                      borderWidth: '2px !important',
                      borderRadius: '8px !important'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${BRAND.accent} !important`
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${BRAND.accent} !important`,
                      borderWidth: '2px !important'
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 16px',
                      fontSize: '16px'
                    }
                  }}
                >
                  {SUPPORTED_LANGUAGE_CODES.map((code) => (
                    <MenuItem key={code} value={code}>{t(`common.languages.${code}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <SalovarePhoneInput
                label={t('teamMembers.registration.phone')}
                required
                autoCompleteSection="team-member"
                countryCode={formData.phoneCountryCode}
                number={formData.phoneNumber}
                onCountryCodeChange={(value) => updateFormData({ phoneCountryCode: value })}
                onNumberChange={(value) => updateFormData({ phoneNumber: value })}
                placeholder={t('teamMembers.registration.phonePlaceholder')}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.genre')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.genre}
                  onChange={(e) => updateFormData({ genre: e.target.value })}
                  displayEmpty
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    height: '48px !important',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${BRAND.line} !important`,
                      borderWidth: '2px !important',
                      borderRadius: '8px !important'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${BRAND.accent} !important`
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${BRAND.accent} !important`,
                      borderWidth: '2px !important'
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 16px',
                      fontSize: '16px'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('teamMembers.registration.selectGenre')}</em>
                  </MenuItem>
                  <MenuItem value="male">{t('teamMembers.registration.male')}</MenuItem>
                  <MenuItem value="female">{t('teamMembers.registration.female')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.birthDate')}
              </Typography>
              <SalovareDatePicker
                value={formData.birthDate}
                onChange={(newValue) => updateFormData({ birthDate: newValue })}
                disableFuture
                maxDate={new Date()}
              />
            </Grid>
          </Grid>

          {acceptsCustomerBookingsSwitch}
        </Box>
      </Box>
    );
  }

  const personalFieldsEnabled = fieldsEnabled && !isExistingMember;
  const emailVerified = emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE
    || (emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING && isExistingMember);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
        {t('teamMembers.registration.steps.step1.title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        {t('teamMembers.registration.steps.step1.description')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('teamMembers.registration.email')} *
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder={t('teamMembers.registration.emailPlaceholder')}
                style={{
                  ...inputStyle(!!errors.email, false),
                  flex: 1
                }}
                onFocus={(e) => { e.target.style.borderColor = BRAND.accent; }}
                onBlur={(e) => { e.target.style.borderColor = errors.email ? '#f44336' : BRAND.line; }}
              />
              <Button
                variant="contained"
                onClick={onCheckEmail}
                disabled={checkingEmail || emailVerified || !isValidEmail(formData.email)}
                startIcon={checkingEmail ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                sx={{
                  minWidth: 120,
                  height: 48,
                  borderRadius: BRAND_RADIUS.button,
                  fontWeight: 600,
                  background: BRAND.accent,
                  color: BRAND.onAccent,
                  boxShadow: 'none',
                  '&:hover': {
                    background: BRAND.accentDark,
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    background: '#ccc',
                    boxShadow: 'none'
                  }
                }}
              >
                {t('teamMembers.registration.emailCheck.check')}
              </Button>
            </Box>
            {errors.email && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {errors.email}
              </Typography>
            )}
          </Grid>
        </Grid>

        {emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE && (
          <Alert severity="success">{t('teamMembers.registration.emailCheck.available')}</Alert>
        )}

        {emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING && isExistingMember && (
          <Alert severity="info">
            {t('teamMembers.registration.emailCheck.existingConfirmed', { name: existingMemberName })}
          </Alert>
        )}

        {emailCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE && (
          <Alert severity="warning">{t('teamMembers.registration.emailCheck.alreadyInStore')}</Alert>
        )}

        {emailCheckStatus === EMAIL_CHECK_STATUS.ERROR && (
          <Alert severity="error">{t('teamMembers.registration.emailCheck.error')}</Alert>
        )}

        {isExistingMember && emailVerified && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('teamMembers.registration.jobTitle')}
              </Typography>
              <input
                type="text"
                value={formData.jobTitle || ''}
                onChange={(e) => updateFormData({ jobTitle: e.target.value })}
                placeholder={t('teamMembers.registration.jobTitlePlaceholder')}
                maxLength={100}
                style={inputStyle(false, false)}
                onFocus={(e) => { e.target.style.borderColor = BRAND.accent; }}
                onBlur={(e) => { e.target.style.borderColor = BRAND.line; }}
              />
            </Grid>
          </Grid>
        )}

        {isExistingMember ? null : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              ...(personalFieldsEnabled ? {} : disabledFieldSx),
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('teamMembers.registration.name')} *
                </Typography>
                <input
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={(e) => updateFormData({ name: e.target.value })}
                  placeholder={t('teamMembers.registration.namePlaceholder')}
                  disabled={!personalFieldsEnabled}
                  style={inputStyle(!!errors.name, !personalFieldsEnabled)}
                  onFocus={(e) => personalFieldsEnabled && (e.target.style.borderColor = BRAND.accent)}
                  onBlur={(e) => personalFieldsEnabled && (e.target.style.borderColor = errors.name ? '#f44336' : BRAND.line)}
                />
                {errors.name && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                    {errors.name}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Box sx={!personalFieldsEnabled ? disabledFieldSx : undefined}>
                  <SalovarePhoneInput
                    label={t('teamMembers.registration.phone')}
                    required
                    autoCompleteSection="team-member"
                    countryCode={formData.phoneCountryCode}
                    number={formData.phoneNumber}
                    onCountryCodeChange={(value) => updateFormData({ phoneCountryCode: value })}
                    onNumberChange={(value) => updateFormData({ phoneNumber: value })}
                    placeholder={t('teamMembers.registration.phonePlaceholder')}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    disabled={!personalFieldsEnabled}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('teamMembers.registration.jobTitle')}
                </Typography>
                <input
                  type="text"
                  value={formData.jobTitle || ''}
                  onChange={(e) => updateFormData({ jobTitle: e.target.value })}
                  placeholder={t('teamMembers.registration.jobTitlePlaceholder')}
                  maxLength={100}
                  disabled={!personalFieldsEnabled}
                  style={inputStyle(false, !personalFieldsEnabled)}
                  onFocus={(e) => personalFieldsEnabled && (e.target.style.borderColor = BRAND.accent)}
                  onBlur={(e) => personalFieldsEnabled && (e.target.style.borderColor = BRAND.line)}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('teamMembers.registration.language')}
                </Typography>
                <FormControl fullWidth disabled={!personalFieldsEnabled}>
                  <Select
                    value={formData.language}
                    onChange={(e) => updateFormData({ language: e.target.value })}
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      height: '48px !important',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${BRAND.line} !important`,
                        borderWidth: '2px !important',
                        borderRadius: '8px !important'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${BRAND.accent} !important`
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${BRAND.accent} !important`,
                        borderWidth: '2px !important'
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 16px',
                        fontSize: '16px'
                      }
                    }}
                  >
                    {SUPPORTED_LANGUAGE_CODES.map((code) => (
                      <MenuItem key={code} value={code}>{t(`common.languages.${code}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('teamMembers.registration.genre')}
                </Typography>
                <FormControl fullWidth disabled={!personalFieldsEnabled}>
                  <Select
                    value={formData.genre}
                    onChange={(e) => updateFormData({ genre: e.target.value })}
                    displayEmpty
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      height: '48px !important',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${BRAND.line} !important`,
                        borderWidth: '2px !important',
                        borderRadius: '8px !important'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${BRAND.accent} !important`
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${BRAND.accent} !important`,
                        borderWidth: '2px !important'
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 16px',
                        fontSize: '16px'
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('teamMembers.registration.selectGenre')}</em>
                    </MenuItem>
                    <MenuItem value="male">{t('teamMembers.registration.male')}</MenuItem>
                    <MenuItem value="female">{t('teamMembers.registration.female')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('teamMembers.registration.birthDate')}
                </Typography>
                <SalovareDatePicker
                  value={formData.birthDate}
                  onChange={(newValue) => updateFormData({ birthDate: newValue })}
                  disableFuture
                  maxDate={new Date()}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {emailVerified && acceptsCustomerBookingsSwitch}
      </Box>
    </Box>
  );
};

export default TeamMemberPersonalInfo;
